/**
 * "Depois da primeira pintura útil" (SPEC 10.4). O que fica abaixo da dobra, ou só enriquece o que
 * já está na tela (gráfico uPlot, widgets lazy, atalhos), só começa a baixar depois que o quadro
 * com os números foi pintado. Assim a rede e a CPU do caminho até o LCP ficam só com a primeira
 * dobra, e o resto chega logo em seguida, com o esqueleto no lugar (CLS zero).
 */
import { useEffect, useState, useSyncExternalStore } from 'react';

/** Quanto esperar, no máximo, pelo registro do LCP do quadro pintado. */
const LCP_WAIT_MS = 150;

/**
 * Roda `cb` depois que o próximo quadro for pintado e apresentado. Devolve o cancelamento.
 * O LCP registra o instante em que o quadro aparece na tela (alguns ms depois da pintura no main
 * thread): onde há `largest-contentful-paint`, espera esse registro (até 150 ms); sem ele, dois
 * quadros.
 */
export function afterNextPaint(cb: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    const t = setTimeout(cb, 0);
    return () => clearTimeout(t);
  }
  const since = performance.now();
  let done = false;
  let raf2: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let observer: PerformanceObserver | null = null;
  const finish = (): void => {
    if (done) return;
    done = true;
    observer?.disconnect();
    if (timer != null) clearTimeout(timer);
    cb();
  };
  const raf = window.requestAnimationFrame(() => {
    raf2 = window.requestAnimationFrame(() => {
      const lcp =
        typeof PerformanceObserver !== 'undefined' &&
        PerformanceObserver.supportedEntryTypes?.includes('largest-contentful-paint');
      if (!lcp) {
        timer = setTimeout(finish, 0);
        return;
      }
      observer = new PerformanceObserver((list) => {
        if (list.getEntries().some((e) => e.startTime >= since)) finish();
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      timer = setTimeout(finish, LCP_WAIT_MS);
    });
  });
  return () => {
    done = true;
    window.cancelAnimationFrame(raf);
    if (raf2 != null) window.cancelAnimationFrame(raf2);
    if (timer != null) clearTimeout(timer);
    observer?.disconnect();
  };
}

let painted = false;
const waiting = new Set<() => void>();

/** A primeira pintura útil já aconteceu (daí em diante, nada mais espera). */
export function isFirstPaintDone(): boolean {
  return painted;
}

function markPainted(): void {
  if (painted) return;
  painted = true;
  for (const cb of [...waiting]) cb();
  waiting.clear();
}

/** Marca a primeira pintura útil da tela (o Hoje marca quando o snapshot pinta). */
export function useMarkPainted(ready = true): void {
  useAfterPaint(ready);
}

const subscribePainted = (cb: () => void): (() => void) => {
  waiting.add(cb);
  return () => waiting.delete(cb);
};

/**
 * `true` depois da primeira pintura útil da tela (marcada por `useAfterPaint`/`useMarkPainted`)
 * ou, se nenhuma tela marcar, `fallbackMs` depois de montar. Não marca nada: é para o shell
 * deixar atalhos e toasts para depois dos números.
 */
export function usePainted(fallbackMs = 1500): boolean {
  const marked = useSyncExternalStore(
    subscribePainted,
    () => painted,
    () => false,
  );
  const [late, setLate] = useState(false);
  useEffect(() => {
    if (marked) return;
    const t = setTimeout(() => setLate(true), fallbackMs);
    return () => clearTimeout(t);
  }, [marked, fallbackMs]);
  return marked || late;
}

/**
 * Roda `cb` quando o app pintar pela primeira vez (ou depois de `timeoutMs`, se nada pintar com
 * `useAfterPaint`, como na TV). Para trabalho de fundo: service worker, pré-cargas.
 */
export function whenPainted(cb: () => void, timeoutMs = 8000): void {
  if (painted) {
    cb();
    return;
  }
  let done = false;
  const run = (): void => {
    if (done) return;
    done = true;
    waiting.delete(run);
    cb();
  };
  waiting.add(run);
  setTimeout(run, timeoutMs);
}

/**
 * `true` a partir do quadro seguinte ao 1º em que `ready` valeu `true` (ex.: o snapshot chegou).
 * Depois da primeira vez no app, vale `true` de saída: trocar de período ou voltar ao Hoje não
 * espera de novo.
 */
export function useAfterPaint(ready = true): boolean {
  const [done, setDone] = useState(painted);
  useEffect(() => {
    if (done || !ready) return;
    return afterNextPaint(() => {
      markPainted();
      setDone(true);
    });
  }, [done, ready]);
  return done;
}
